import React, { useState } from "react";
import { InputNumber, Tag, theme, Typography } from "antd";

const { Text, Title } = Typography;

export type PricingValueType = "Fixed" | "Percentage";

export interface PricingRowState {
  valueType: PricingValueType;
  floor: number;
  vendor: number;
  partnerDefault: number;
  max: number;
}

export interface PricingRow {
  key: string;
  label: string;
  state: PricingRowState;
  error?: string;
}

const VENDOR_COLOR = "#f5a623";
const KEEPS_GRADIENT =
  "linear-gradient(90deg, #3867ff 0%, #8b3dff 62%, #cb22ff 100%)";
const VENDOR_HATCH =
  "repeating-linear-gradient(135deg, rgb(255, 209, 138) 0px, rgb(255, 209, 138) 4px, rgb(255, 184, 92) 4px, rgb(255, 184, 92) 8px)";
const MARKUP_HATCH =
  "repeating-linear-gradient(135deg, rgb(217, 208, 255) 0px, rgb(217, 208, 255) 4px, rgb(201, 189, 255) 4px, rgb(201, 189, 255) 8px)";

// Bucket, Klearly floor, [SW vendor], [Partner default], Max, breakdown toggle.
const gridTemplate = (showVendor: boolean, showPartnerDefault: boolean) =>
  [
    "minmax(150px, 1.25fr)",
    "190px",
    showVendor ? "116px" : undefined,
    showPartnerDefault ? "150px" : undefined,
    "112px",
    "40px",
  ]
    .filter(Boolean)
    .join(" ");

const formatPricingNumber = (value: number) =>
  value === 0 ? "0.00" : value.toFixed(3).replace(/\.?0+$/, "");

export const formatPricingInput = (
  value: number | string | undefined,
  info: { userTyping: boolean; input: string }
) => {
  if (info.userTyping) return info.input;
  const numericValue = Number(value);
  return value !== undefined && Number.isFinite(numericValue)
    ? formatPricingNumber(numericValue)
    : "";
};

const fmt = (value: number, valueType: PricingValueType) =>
  valueType === "Fixed"
    ? `€${formatPricingNumber(value)}`
    : `${formatPricingNumber(value)}%`;

const fmtDelta = (delta: number, valueType: PricingValueType) => {
  const sign = delta >= 0 ? "+" : "";
  return valueType === "Fixed"
    ? `${sign}€${formatPricingNumber(delta)}`
    : `${sign}${formatPricingNumber(delta)}%`;
};

const clampPercent = (value: number) => Math.max(0, Math.min(100, value));

const anchorTransform = (pos: number) => {
  if (pos < 15) return "translateX(0)";
  if (pos > 85) return "translateX(-100%)";
  return "translateX(-50%)";
};

interface PricingBucketsTableProps {
  title: string;
  subtitle: string;
  rows: PricingRow[];
  onChange: (key: string, patch: Partial<PricingRowState>) => void;
  dirtyKeys?: Set<string>;
  showVendor?: boolean;
  showPartnerDefault?: boolean;
  sharedPricingDisplayOnly?: boolean;
  // false when nested inside another bordered card (e.g. the "New partner
  // price range" flow) so the two don't render as a double border.
  bordered?: boolean;
}

export default function PricingBucketsTable({
  title,
  subtitle,
  rows,
  onChange,
  dirtyKeys,
  showVendor = true,
  showPartnerDefault = true,
  sharedPricingDisplayOnly = false,
  bordered = true,
}: PricingBucketsTableProps) {
  const { token } = theme.useToken();
  const [expandedRows, setExpandedRows] = useState<Record<string, boolean>>({});

  const anyExpanded = rows.some((r) => expandedRows[r.key]);

  const toggleAllExpanded = (next: boolean) =>
    setExpandedRows(
      rows.reduce<Record<string, boolean>>((acc, r) => {
        acc[r.key] = next;
        return acc;
      }, {})
    );

  const toggleRow = (key: string) =>
    setExpandedRows((prev) => ({ ...prev, [key]: !prev[key] }));

  return (
    <div
      style={
        bordered
          ? {
              margin: "0 24px 20px",
              border: `1px solid ${token.colorBorderSecondary}`,
              borderRadius: 14,
              overflow: "hidden",
              background: token.colorBgContainer,
            }
          : {
              background: token.colorBgContainer,
            }
      }
    >
      <div
        style={{
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center",
          padding: "16px 22px",
          borderBottom: `1px solid ${token.colorBorderSecondary}`,
        }}
      >
        <div>
          <Title level={5} style={{ margin: 0 }}>
            {title}
          </Title>
          <Text type="secondary">{subtitle}</Text>
        </div>
        <button
          type="button"
          onClick={() => toggleAllExpanded(!anyExpanded)}
          style={{
            display: "inline-flex",
            alignItems: "center",
            justifyContent: "center",
            gap: 7,
            minWidth: 130,
            height: 28,
            padding: "0 12px",
            borderRadius: 8,
            border: `1px solid ${
              anyExpanded
                ? token.colorPrimaryBorder
                : token.colorBorderSecondary
            }`,
            color: anyExpanded ? token.colorPrimary : token.colorTextSecondary,
            background: anyExpanded
              ? token.colorPrimaryBg
              : token.colorBgContainer,
            cursor: "pointer",
            fontWeight: 500,
            fontSize: 12,
            lineHeight: 1,
          }}
        >
          {anyExpanded ? "Hide breakdown" : "Show breakdown"}
        </button>
      </div>

      <div
        style={{
          display: "grid",
          gridTemplateColumns: gridTemplate(showVendor, showPartnerDefault),
          gap: 14,
          padding: "10px 22px",
          background: token.colorFillAlter,
          color: token.colorTextTertiary,
          fontSize: 11,
          fontWeight: 700,
          letterSpacing: 1,
          textTransform: "uppercase",
        }}
      >
        <span>Bucket</span>
        <span style={{ color: token.colorPrimary }}>• Klearly floor</span>
        {showVendor && <span style={{ color: VENDOR_COLOR }}>• SW vendor</span>}
        {showPartnerDefault && (
          <span style={{ color: token.colorText }}>Partner default</span>
        )}
        <span>Max</span>
        <span />
      </div>

      {rows.map((row) => (
        <BucketRow
          key={row.key}
          label={row.label}
          bucket={row.state}
          error={row.error}
          dirty={!!dirtyKeys?.has(row.key)}
          showVendor={showVendor}
          showPartnerDefault={showPartnerDefault}
          sharedPricingDisplayOnly={sharedPricingDisplayOnly}
          expanded={!!expandedRows[row.key]}
          onToggle={() => toggleRow(row.key)}
          onChange={(patch) => onChange(row.key, patch)}
        />
      ))}
    </div>
  );
}

export function ValueTypeToggle({
  valueType,
  onChange,
  disabled = false,
}: {
  valueType: PricingValueType;
  onChange: (next: PricingValueType) => void;
  disabled?: boolean;
}) {
  const { token } = theme.useToken();
  const option = (label: string, type: PricingValueType) => {
    const active = valueType === type;
    return (
      <button
        type="button"
        disabled={disabled}
        onClick={() => onChange(type)}
        style={{
          width: 26,
          height: 30,
          border: 0,
          cursor: disabled ? "default" : "pointer",
          fontWeight: 600,
          fontSize: 13,
          color: active ? token.colorText : token.colorTextTertiary,
          background: active ? token.colorBgContainer : "transparent",
          borderRadius: 5,
          boxShadow: active ? token.boxShadowTertiary : "none",
        }}
      >
        {label}
      </button>
    );
  };
  return (
    <div
      style={{
        display: "inline-flex",
        padding: 2,
        gap: 2,
        borderRadius: 7,
        background: token.colorFillSecondary,
      }}
    >
      {option("€", "Fixed")}
      {option("%", "Percentage")}
    </div>
  );
}

function BucketRow({
  label,
  bucket,
  error,
  dirty,
  showVendor,
  showPartnerDefault,
  sharedPricingDisplayOnly,
  expanded,
  onToggle,
  onChange,
}: {
  label: string;
  bucket: PricingRowState;
  error?: string;
  dirty?: boolean;
  showVendor: boolean;
  showPartnerDefault: boolean;
  sharedPricingDisplayOnly: boolean;
  expanded: boolean;
  onToggle: () => void;
  onChange: (patch: Partial<PricingRowState>) => void;
}) {
  const { token } = theme.useToken();
  const { valueType, floor, vendor, partnerDefault, max } = bucket;
  const markup = partnerDefault - floor;
  const maxBase = max > 0 ? max : Math.max(partnerDefault, floor, 1);
  const keeps = Math.max(0, floor - (showVendor ? vendor : 0));
  const keepsPos = clampPercent((keeps / maxBase) * 100);
  const floorPos = clampPercent((floor / maxBase) * 100);
  const valuePos = clampPercent((partnerDefault / maxBase) * 100);
  const labelsCollide = Math.abs(valuePos - floorPos) < 14;
  const collisionLabelPos = clampPercent(Math.min(floorPos, valuePos) - 4);

  const numberAddons =
    valueType === "Fixed" ? { addonBefore: "€" } : { addonAfter: "%" };

  return (
    <div
      style={{
        borderTop: `1px solid ${token.colorBorderSecondary}`,
        padding: "12px 22px",
      }}
    >
      <div
        style={{
          display: "grid",
          gridTemplateColumns: gridTemplate(showVendor, showPartnerDefault),
          gap: 14,
          alignItems: "center",
        }}
      >
        <div>
          <Text strong>{label}</Text>
          {dirty && (
            <Tag
              color="warning"
              bordered={false}
              style={{ marginLeft: 8, fontSize: 10, lineHeight: "16px" }}
            >
              Modified
            </Tag>
          )}
          {error && (
            <Text
              type="danger"
              style={{ display: "block", marginTop: 4, fontSize: 12 }}
            >
              {error}
            </Text>
          )}
        </div>

        <div style={{ display: "flex", gap: 8, alignItems: "center" }}>
          <InputNumber
            controls={false}
            value={floor}
            precision={3}
            formatter={formatPricingInput}
            min={0}
            {...numberAddons}
            style={{ flex: 1, minWidth: 0 }}
            onChange={(next) => onChange({ floor: Number(next ?? 0) })}
          />
          <ValueTypeToggle
            valueType={valueType}
            disabled={sharedPricingDisplayOnly}
            onChange={(next) => onChange({ valueType: next })}
          />
        </div>

        {showVendor &&
          (sharedPricingDisplayOnly ? (
            <PricingValueDisplay value={vendor} valueType={valueType} />
          ) : (
            <InputNumber
              controls={false}
              value={vendor}
              precision={3}
              formatter={formatPricingInput}
              min={0}
              {...numberAddons}
              style={{ width: "100%" }}
              onChange={(next) => onChange({ vendor: Number(next ?? 0) })}
            />
          ))}

        {showPartnerDefault && (
          <div>
            {sharedPricingDisplayOnly ? (
              <PricingValueDisplay
                value={partnerDefault}
                valueType={valueType}
                error={!!error}
              />
            ) : (
              <InputNumber
                controls={false}
                value={partnerDefault}
                precision={3}
                formatter={formatPricingInput}
                min={0}
                status={error ? "error" : undefined}
                {...numberAddons}
                style={{ width: "100%" }}
                onChange={(next) =>
                  onChange({ partnerDefault: Number(next ?? 0) })
                }
              />
            )}
            <Text
              style={{
                display: "block",
                marginTop: 5,
                color: error ? token.colorError : token.colorPrimary,
                fontSize: 12,
              }}
            >
              {fmtDelta(markup, valueType)} markup
              {!error && floor > 0 && (
                <span style={{ color: token.colorTextTertiary }}>
                  {" "}
                  · {Math.round((markup / floor) * 100)}%
                </span>
              )}
            </Text>
          </div>
        )}

        <InputNumber
          controls={false}
          value={max}
          precision={3}
          formatter={formatPricingInput}
          min={0}
          {...numberAddons}
          style={{ width: "100%" }}
          onChange={(next) => onChange({ max: Number(next ?? 0) })}
        />

        <button
          type="button"
          onClick={onToggle}
          title={expanded ? "Hide split breakdown" : "Show split breakdown"}
          aria-label="Toggle split breakdown"
          style={{
            display: "inline-flex",
            alignItems: "center",
            justifyContent: "center",
            width: 24,
            height: 24,
            padding: 0,
            borderRadius: 6,
            border: "1px solid transparent",
            color: expanded ? token.colorPrimary : token.colorTextTertiary,
            background: expanded ? token.colorPrimaryBg : "transparent",
            cursor: "pointer",
            transition: "0.15s",
          }}
        >
          <svg
            width="12"
            height="12"
            viewBox="0 0 12 12"
            fill="none"
            style={{
              transform: expanded ? "none" : "rotate(180deg)",
              transition: "transform 0.15s",
            }}
          >
            <path
              d="M3 7.5L6 4.5L9 7.5"
              stroke="currentColor"
              strokeWidth="1.4"
              strokeLinecap="round"
              strokeLinejoin="round"
            />
          </svg>
        </button>
      </div>

      {expanded && (
        <div style={{ marginTop: 10 }}>
          <div
            style={{
              display: "flex",
              justifyContent: "space-between",
              fontSize: 12,
              fontWeight: 600,
              marginBottom: 4,
            }}
          >
            <span style={{ display: "flex", gap: 14 }}>
              <span style={{ color: token.colorPrimary }}>
                ■ KLEARLY KEEPS {fmt(keeps, valueType)}
              </span>
              {showVendor && (
                <span style={{ color: VENDOR_COLOR }}>
                  ■ VENDOR {fmt(vendor, valueType)}
                </span>
              )}
            </span>
            <span style={{ color: token.colorTextTertiary }}>
              MAX CAP {fmt(max, valueType)}
            </span>
          </div>
          <div style={{ position: "relative", height: 12 }}>
            <div
              style={{
                position: "absolute",
                top: "50%",
                left: 0,
                right: 0,
                height: 7,
                transform: "translateY(-50%)",
                borderRadius: 8,
                background: token.colorFillSecondary,
                overflow: "hidden",
              }}
            >
              <div
                style={{
                  position: "absolute",
                  left: 0,
                  top: 0,
                  bottom: 0,
                  width: `${keepsPos}%`,
                  background: KEEPS_GRADIENT,
                  transition: "width 0.3s",
                }}
              />
              {showVendor && (
                <div
                  style={{
                    position: "absolute",
                    left: `${keepsPos}%`,
                    top: 0,
                    bottom: 0,
                    width: `${Math.max(0, floorPos - keepsPos)}%`,
                    background: VENDOR_HATCH,
                    transition: "width 0.3s, left 0.3s",
                  }}
                />
              )}
              {showPartnerDefault && (
                <div
                  style={{
                    position: "absolute",
                    left: `${floorPos}%`,
                    top: 0,
                    bottom: 0,
                    width: `${Math.max(0, valuePos - floorPos)}%`,
                    background: MARKUP_HATCH,
                    transition: "width 0.3s, left 0.3s",
                  }}
                />
              )}
            </div>
            <span
              style={{
                position: "absolute",
                left: `${floorPos}%`,
                top: "50%",
                width: 12,
                height: 12,
                borderRadius: "50%",
                background: token.colorPrimary,
                transform: "translate(-50%, -50%)",
                border: `2px solid ${token.colorBgContainer}`,
              }}
            />
            {showPartnerDefault && (
              <span
                style={{
                  position: "absolute",
                  left: `${valuePos}%`,
                  top: "50%",
                  width: 10,
                  height: 10,
                  borderRadius: "50%",
                  background: token.colorText,
                  transform: "translate(-50%, -50%)",
                  border: `2px solid ${token.colorBgContainer}`,
                }}
              />
            )}
          </div>
          {showPartnerDefault && (
            <div style={{ position: "relative", height: 38, marginTop: 6 }}>
              {labelsCollide ? (
                <div
                  style={{
                    position: "absolute",
                    top: 0,
                    left: `${collisionLabelPos}%`,
                    transform: anchorTransform(collisionLabelPos),
                    display: "flex",
                    gap: 18,
                    whiteSpace: "nowrap",
                    fontSize: 11,
                    fontWeight: 700,
                    lineHeight: 1.4,
                  }}
                >
                  <span style={{ color: token.colorPrimary }}>
                    PARTNER MARKUP
                    <br />
                    {fmtDelta(markup, valueType)}
                  </span>
                  <span style={{ color: token.colorTextSecondary }}>
                    MERCHANT PAYS
                    <br />
                    <span style={{ color: token.colorText, fontSize: 13 }}>
                      {fmt(partnerDefault, valueType)}
                    </span>
                  </span>
                </div>
              ) : (
                <>
                  <div
                    style={{
                      position: "absolute",
                      top: 0,
                      left: `${floorPos}%`,
                      transform: anchorTransform(floorPos),
                      whiteSpace: "nowrap",
                      fontSize: 11,
                      fontWeight: 700,
                      lineHeight: 1.4,
                      color: token.colorPrimary,
                    }}
                  >
                    PARTNER MARKUP
                    <br />
                    {fmtDelta(markup, valueType)}
                  </div>
                  <div
                    style={{
                      position: "absolute",
                      top: 0,
                      left: `${valuePos}%`,
                      transform: anchorTransform(valuePos),
                      whiteSpace: "nowrap",
                      fontSize: 11,
                      fontWeight: 700,
                      lineHeight: 1.4,
                      color: token.colorTextSecondary,
                    }}
                  >
                    MERCHANT PAYS
                    <br />
                    <span style={{ color: token.colorText, fontSize: 13 }}>
                      {fmt(partnerDefault, valueType)}
                    </span>
                  </div>
                </>
              )}
            </div>
          )}
        </div>
      )}
    </div>
  );
}

function PricingValueDisplay({
  value,
  valueType,
  error = false,
}: {
  value: number;
  valueType: PricingValueType;
  error?: boolean;
}) {
  return (
    <Text type={error ? "danger" : undefined}>
      {valueType === "Fixed" ? "€" : ""}
      {formatPricingNumber(value)}
      {valueType === "Percentage" ? "%" : ""}
    </Text>
  );
}
